import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  View,
  TextInput,
  Text,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import { observer } from "mobx-react";
import Colors from "../../../Resources/Color";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../../_layout";
import { useCoachStore } from "../../contexts/CoachStoreContext";
import { useToast } from "../../contexts/ToastContext";
import { markTutorialStepCompleted } from "../../utils/tutorialUtils";
import { getExerciseSearchService } from "./ExerciseSearchService";
import Exercise from "../../Firestore/Exercise";
import Serie from "../../Firestore/Serie";
import Tooltip from "../../Components/Tooltip";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import ExerciseManualPickerModal from "../ExerciseManualPickerModal";

// Import extracted components and utilities
import { PILL_COLORS } from "./constants";
import { buildExerciseNameWithModifiers } from "./utils/searchUtils";
import { WorkoutLimits } from "../../../Resources/Constants";
import {
  getScrollableNodeFromRef,
  isUsableReferenceElement,
  isEditableEventTarget,
} from "./utils/floatingUtils";
import { canImportWorkoutExercises } from "./utils/workoutImportUtils";
import { RecentlyUsedRow } from "./components/RecentlyUsedRow";
import { SuggestionRow } from "./components/SuggestionRow";
import { WorkoutLibraryRow } from "./components/WorkoutLibraryRow";
import { SlotRow } from "./components/SlotRow";
import { DeleteConfirmationView } from "./components/DeleteConfirmationView";
import Attribute from "../../Firestore/Attribute";

const FLOATING_SEARCH_Z_INDEX = 130000;

/**
 * ExerciseSearchFloating
 *
 * A floating search component that displays exercise and modifier suggestions
 * with intelligent parsing of compound exercise names (e.g., "Bench Press 1ct Pause Close Grip").
 *
 * Features:
 * - Real-time search with debouncing
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - Highlighted matching words in suggestions
 * - Pills showing main exercise type and modifiers
 * - Category-aware modifier suggestions
 * - Managed by Coach store to ensure only one floating search is open at a time
 */

const ExerciseSearchFloating = observer(
  ({
    isOpen,
    onClose,
    referenceElement,
    portalRoot = null,
    onSelectExercise,
    instanceId,
    suggestionContext,
    crossAxisOffset = 0,
    initialSearchText = "",
    slotExercise = null,
    primarySlotExercise = null,
    block = null,
    protocolSlot = null,
    currentWeek = null,
    suggestionsMode = "protocol", // "protocol" (default) or "workout"
    currentWorkout = null, // Required when suggestionsMode is "workout"
    exerciseIndex = 0, // Position of exercise in workout (for position boost)
    allowShiftEnterReopen = false,
    onRequestAddExercise = null,
  }) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const { screenSize } = useScreenDimensions();
    const { isMobile } = useDevice();
    const coachStore = useCoachStore();
    const insets = useSafeAreaInsets();
    const { showToast } = useToast();

    const MANUAL_HOVER_COLOR = "#2E5E6E";

    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState([]);
    const [recentlyUsedExercises, setRecentlyUsedExercises] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [isShowingWorkoutSuggestions, setIsShowingWorkoutSuggestions] =
      useState(false);
    const [slotSuggestions, setSlotSuggestions] = useState([]);
    const [deleteButtonHovered, setDeleteButtonHovered] = useState(false);
    const [closeButtonHovered, setCloseButtonHovered] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showManualPicker, setShowManualPicker] = useState(false);
    const [manualIconHovered, setManualIconHovered] = useState(false);
    const [clearIconHovered, setClearIconHovered] = useState(false);
    const [manualLinkFooterHovered, setManualLinkFooterHovered] =
      useState(false);
    const [manualLinkEmptyHovered, setManualLinkEmptyHovered] = useState(false);

    const isEditingExistingExercise = slotExercise != null;
    const shouldAllowWorkoutLibraryResults =
      suggestionsMode === "workout" && !isEditingExistingExercise;

    // Track the *visual* viewport height (best signal for on-screen keyboards on mobile browsers).
    // Fallback to window.innerHeight when VisualViewport isn't available.
    const [visualViewportHeight, setVisualViewportHeight] = useState(null);

    const searchInputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const scrollViewRef = useRef(null);
    const initialSearchTextRef = useRef(""); // Store the initial search text
    const keyboardNavigationRef = useRef(false);

    const searchService = useMemo(() => getExerciseSearchService(), []);
    const sanitizeResultsForContext = useCallback(
      (nextResults) => {
        if (!Array.isArray(nextResults)) return [];
        if (!isEditingExistingExercise) return nextResults;
        return nextResults.filter((result) => result?.type !== "workout");
      },
      [isEditingExistingExercise],
    );

    // Get predefined data
    const predefinedExercises = useMemo(
      () => coachStore.customPredefinedExercises,
      [coachStore],
    );
    const predefinedAttributes = useMemo(
      () => coachStore.customPredefinedAttributes,
      [coachStore],
    );

    // Floating UI setup
    const { refs, floatingStyles, context, update } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        // This is triggered by FloatingUI interactions (e.g., clicking outside)
        if (!open) {
          onClose();
        }
      },
      strategy: "fixed",
      placement: "bottom-start",
      middleware: [
        offset({ mainAxis: 6, crossAxis: crossAxisOffset }),
        flip({
          fallbackAxisSideDirection: "end",
          padding: 8,
        }),
        shift({ padding: 8, crossAxis: true }),
      ],
      whileElementsMounted: autoUpdate,
    });

    useEffect(() => {
      if (!isOpen || !isMobile) {
        setVisualViewportHeight(null);
        return;
      }

      if (typeof window === "undefined") {
        return;
      }

      const vv = window.visualViewport;

      const updateViewport = () => {
        const nextHeight = vv?.height ?? window.innerHeight;
        setVisualViewportHeight(nextHeight);
        update?.();
      };

      updateViewport();

      // Mobile only: listen to visual viewport changes (keyboard open/close).
      if (vv) {
        vv.addEventListener("resize", updateViewport);
        vv.addEventListener("scroll", updateViewport);
        return () => {
          vv.removeEventListener("resize", updateViewport);
          vv.removeEventListener("scroll", updateViewport);
        };
      }

      // Fallback for browsers without VisualViewport.
      window.addEventListener("resize", updateViewport);
      return () => window.removeEventListener("resize", updateViewport);
    }, [isOpen, isMobile, update]);

    const floatingContainerStyle = useMemo(() => {
      const baseStyleCommon = {
        backgroundColor: Colors.whiteColor,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: Colors.borderColor,
        outlineStyle: "none",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        overflow: "hidden",
        minWidth: screenSize === "small" ? 320 : 400,
      };

      const defaultMaxHeight = 302;
      const defaultMinHeight = showDeleteConfirmation ? 0 : 180;

      let maxHeight = defaultMaxHeight;
      let minHeight = defaultMinHeight;

      let viewportHeight = null;
      let viewportMaxHeight = null;
      let safeArea = 0;
      const viewportPadding = 12;

      if (isMobile) {
        viewportHeight =
          visualViewportHeight ??
          (typeof window !== "undefined" ? window.innerHeight : null);
        safeArea = (insets?.top || 0) + (insets?.bottom || 0);
        viewportMaxHeight = viewportHeight
          ? Math.max(120, viewportHeight - safeArea - viewportPadding)
          : null;

        if (viewportMaxHeight) {
          maxHeight = Math.min(defaultMaxHeight, viewportMaxHeight);
          minHeight = showDeleteConfirmation ? 0 : Math.min(180, maxHeight);
        }
      }

      const baseStyle = {
        ...baseStyleCommon,
        minHeight,
        maxHeight,
      };

      if (showManualPicker) {
        const manualHeight =
          isMobile && viewportHeight
            ? Math.round((viewportHeight - safeArea - viewportPadding) * 0.96)
            : "96vh";

        const manualMaxHeight = isMobile
          ? viewportMaxHeight
            ? viewportMaxHeight
            : "100%"
          : 440;

        return {
          ...baseStyle,
          width: "92vw",
          maxWidth: 600,
          height: manualHeight,
          maxHeight: manualMaxHeight,
        };
      }

      return {
        ...baseStyle,
        maxWidth: screenSize === "small" ? "96%" : 600,
      };
    }, [
      insets?.bottom,
      insets?.top,
      isMobile,
      screenSize,
      showDeleteConfirmation,
      showManualPicker,
      visualViewportHeight,
    ]);

    const role = useRole(context);
    const { getFloatingProps } = useInteractions([role]);

    const resetAndClose = useCallback(() => {
      setSearchQuery("");
      setResults([]);
      setRecentlyUsedExercises([]);
      setSelectedIndex(0);
      setShowDeleteConfirmation(false);
      setShowManualPicker(false);
      onClose?.();
    }, [onClose]);

    // Set reference element
    useEffect(() => {
      if (isUsableReferenceElement(referenceElement)) {
        refs.setReference(referenceElement);
        return;
      }

      // Clear reference if it becomes unavailable (e.g., navigation away).
      refs.setReference(null);
    }, [referenceElement, refs]);

    // If opened without a usable reference element, close immediately.
    // This avoids the floating rendering at the top-left when the anchor is off-screen.
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      let cancelled = false;

      // On RN-web, the anchor can exist but briefly report a 0x0 rect on the
      // first frame after mount. Allow a couple of RAF retries before closing.
      let attempts = 0;
      const maxAttempts = 3;
      let rafId = null;

      const check = () => {
        if (cancelled) {
          return;
        }

        if (!isUsableReferenceElement(referenceElement)) {
          attempts += 1;
          if (attempts >= maxAttempts) {
            onClose?.();
            return;
          }

          if (typeof requestAnimationFrame !== "undefined") {
            rafId = requestAnimationFrame(check);
            return;
          }

          setTimeout(check, 0);
          return;
        }

        update?.();
      };

      if (typeof requestAnimationFrame !== "undefined") {
        rafId = requestAnimationFrame(check);
        return () => {
          cancelled = true;
          if (rafId != null) {
            cancelAnimationFrame(rafId);
          }
        };
      }

      const timeoutId = setTimeout(check, 0);
      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    }, [isOpen, referenceElement, onClose, update]);

    // Continuously monitor reference element while search is open
    // Auto-close if reference element disappears (e.g., DOM element removed)
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      // Check periodically if reference element is still usable
      // Use a reasonable interval (100ms) to balance responsiveness and performance
      const intervalId = setInterval(() => {
        if (!isUsableReferenceElement(referenceElement)) {
          // Reference element is no longer usable, close the search
          onClose?.();
        }
      }, 200);

      return () => {
        clearInterval(intervalId);
      };
    }, [isOpen, referenceElement, onClose]);

    useEffect(() => {
      update?.();
    }, [showManualPicker, update]);

    // Handle isOpen changes (when parent opens/closes the floating search)
    useEffect(() => {
      if (!isOpen) {
        // Reset state when closed
        setSearchQuery("");
        setResults([]);
        setRecentlyUsedExercises([]);
        setSlotSuggestions([]);
        setSelectedIndex(0);
        setShowManualPicker(false);
        setIsShowingWorkoutSuggestions(false);
        initialSearchTextRef.current = "";
      } else {
        // Workout mode: use workout-based suggestions + slot suggestions
        if (suggestionsMode === "workout" && currentWorkout && block) {
          // Only show slot suggestions when adding a new exercise (not editing)
          if (!slotExercise) {
            // Get available slots for this week
            const currentWeekNum = currentWorkout.week;
            const availableSlots = searchService.getAvailableSlots(
              block,
              currentWeekNum,
            );
            setSlotSuggestions(availableSlots);
          } else {
            setSlotSuggestions([]);
          }

          // Get workout suggestions
          const workoutSuggestions = searchService.getWorkoutSuggestions(
            block,
            currentWorkout,
            slotExercise,
            exerciseIndex ?? 0,
          );
          const fallbackSuggestions =
            workoutSuggestions.length === 0
              ? searchService.getWorkoutFallbackSuggestions(predefinedExercises)
              : workoutSuggestions;
          setResults(fallbackSuggestions);
          setRecentlyUsedExercises([]);
          setIsShowingWorkoutSuggestions(true);
          return;
        }

        setIsShowingWorkoutSuggestions(false);
        setSlotSuggestions([]);

        // Protocol mode: check for recently used first, then show suggestions
        if (block && suggestionContext) {
          const recentExercises = searchService.getRecentlyUsedExercises(
            block,
            suggestionContext,
            protocolSlot,
            currentWeek,
            slotExercise,
          );

          console.log("Recently used exercises found:", recentExercises.length);

          if (recentExercises.length > 0) {
            setRecentlyUsedExercises(recentExercises);
            setResults([]);
            return;
          }
        }

        // Fall back to suggestions if no recent exercises
        if (suggestionContext) {
          console.log("Loading default suggestions for:", suggestionContext);
          const suggestions = searchService.getSuggestions(
            suggestionContext,
            predefinedExercises,
            slotExercise,
          );
          setResults(suggestions);
          setRecentlyUsedExercises([]);
        }
      }
    }, [
      isOpen,
      block,
      suggestionContext,
      protocolSlot,
      currentWeek,
      searchService,
      predefinedExercises,
      suggestionsMode,
      currentWorkout,
      exerciseIndex,
    ]);

    // Focus input and set initial search text when opened
    useEffect(() => {
      if (isOpen) {
        // Store the initial search text (always update, even if empty)
        initialSearchTextRef.current = initialSearchText || "";
        setSearchQuery(initialSearchText || "");

        // NOTE: On mobile browsers (especially iOS Safari), programmatic focus
        // from effects/timeouts is often blocked unless it happens directly
        // inside the user's tap event. Attempting it can also cause keyboard
        // flicker (opens then immediately closes). So we only auto-focus on
        // non-mobile; mobile users can tap the input to focus.
        if (!isMobile) {
          let raf1 = null;
          let raf2 = null;
          const focusNow = () => {
            try {
              searchInputRef.current?.focus?.();
            } catch (_e) {
              // no-op
            }
          };

          // Focus can be timing-sensitive on RN web, especially when the floating
          // opens in the same render as other state updates. A short RAF retry
          // keeps existing behavior but makes it more reliable.
          if (typeof requestAnimationFrame !== "undefined") {
            raf1 = requestAnimationFrame(() => {
              focusNow();
              raf2 = requestAnimationFrame(() => focusNow());
            });
          } else {
            setTimeout(focusNow, 0);
          }

          return () => {
            if (raf1 != null) cancelAnimationFrame(raf1);
            if (raf2 != null) cancelAnimationFrame(raf2);
          };
        }
      }
    }, [isOpen, initialSearchText, isMobile]);

    // Perform search with debouncing
    const performSearch = useCallback(
      (query) => {
        const trimmedQuery = query.trim();

        // Check if the query matches the initial search text (exercise name)
        const matchesInitialText =
          initialSearchTextRef.current &&
          trimmedQuery === initialSearchTextRef.current.trim();

        // Show suggestions/recently used if query is empty
        if (!trimmedQuery) {
          // Workout mode: use workout-based suggestions + slot suggestions
          if (suggestionsMode === "workout" && currentWorkout && block) {
            // Only show slot suggestions when adding a new exercise (not editing)
            if (!slotExercise) {
              // Get available slots for this week
              const currentWeekNum = currentWorkout.week;
              const availableSlots = searchService.getAvailableSlots(
                block,
                currentWeekNum,
              );
              setSlotSuggestions(availableSlots);
            } else {
              setSlotSuggestions([]);
            }

            const workoutSuggestions = searchService.getWorkoutSuggestions(
              block,
              currentWorkout,
              slotExercise,
              exerciseIndex ?? 0,
            );
            const fallbackSuggestions =
              workoutSuggestions.length === 0
                ? searchService.getWorkoutFallbackSuggestions(
                    predefinedExercises,
                  )
                : workoutSuggestions;
            setResults(fallbackSuggestions);
            setRecentlyUsedExercises([]);
            setIsSearching(false);
            setSelectedIndex(0);
            setIsShowingWorkoutSuggestions(true);
            return;
          }

          setIsShowingWorkoutSuggestions(false);
          setSlotSuggestions([]);

          // Protocol mode: check for recently used exercises first
          if (block && suggestionContext) {
            const recentExercises = searchService.getRecentlyUsedExercises(
              block,
              suggestionContext,
              protocolSlot,
              currentWeek,
              slotExercise,
            );
            if (recentExercises.length > 0) {
              setRecentlyUsedExercises(recentExercises);
              setResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
              return;
            }
          }

          // Fall back to suggestions
          if (suggestionContext) {
            const suggestions = searchService.getSuggestions(
              suggestionContext,
              predefinedExercises,
              slotExercise,
            );
            setResults(suggestions);
            setRecentlyUsedExercises([]);
          } else {
            setResults([]);
            setRecentlyUsedExercises([]);
          }
          setIsSearching(false);
          setSelectedIndex(0);
          return;
        }

        // If query matches initial exercise name, try recently used/workout suggestions first
        if (matchesInitialText) {
          // Workout mode: show workout suggestions + slot suggestions
          if (suggestionsMode === "workout" && currentWorkout && block) {
            // Only show slot suggestions when adding a new exercise (not editing)
            let availableSlots = [];
            if (!slotExercise) {
              // Get available slots for this week
              const currentWeekNum = currentWorkout.week;
              availableSlots = searchService.getAvailableSlots(
                block,
                currentWeekNum,
              );
              setSlotSuggestions(availableSlots);
            } else {
              setSlotSuggestions([]);
            }

            const workoutSuggestions = searchService.getWorkoutSuggestions(
              block,
              currentWorkout,
              slotExercise,
              exerciseIndex ?? 0,
            );
            if (workoutSuggestions.length > 0 || availableSlots.length > 0) {
              setResults(workoutSuggestions);
              setRecentlyUsedExercises([]);
              setIsSearching(false);
              setSelectedIndex(0);
              setIsShowingWorkoutSuggestions(true);
              return;
            }
          }

          setIsShowingWorkoutSuggestions(false);
          setSlotSuggestions([]);

          // Protocol mode: try recently used
          if (block && suggestionContext) {
            const recentExercises = searchService.getRecentlyUsedExercises(
              block,
              suggestionContext,
              protocolSlot,
              currentWeek,
              slotExercise,
            );
            if (recentExercises.length > 0) {
              setRecentlyUsedExercises(recentExercises);
              setResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
              return;
            }
          }
          // If no recently used, fall through to normal search below
        }

        try {
          const searchResults = searchService.search(
            trimmedQuery,
            predefinedExercises,
            predefinedAttributes,
            {
              context: suggestionContext,
              block,
              protocolSlot,
              currentWeek,
              currentExercise: slotExercise,
            },
          );

          // In workout mode, also search workout library and slots, then merge results
          if (suggestionsMode === "workout" && block) {
            let mergedResults = [...searchResults];

            // Search workout library if available
            // Support both client blocks (block.client.coach) and library blocks (block.coach)
            const coach = block?.client?.coach || block?.coach;
            if (coach && shouldAllowWorkoutLibraryResults) {
              const workoutLibrary = coach.workoutLibrary || [];
              const workoutResults = searchService.searchWorkoutLibrary(
                trimmedQuery,
                workoutLibrary,
                3, // max 3 workout results
                {
                  excludeWorkoutId: currentWorkout?.id ?? null,
                },
              );

              if (workoutResults.length > 0) {
                mergedResults = searchService.mergeResultsWithWorkouts(
                  mergedResults,
                  workoutResults,
                );
              }
            }

            // Search available slots (only when adding a new exercise, not editing)
            if (!slotExercise) {
              const currentWeekNum = currentWorkout?.week;
              if (currentWeekNum) {
                const slotResults = searchService.searchSlotsForWorkout(
                  trimmedQuery,
                  block,
                  currentWeekNum,
                  t,
                  language,
                );

                if (slotResults.length > 0) {
                  mergedResults = searchService.mergeResultsWithSlots(
                    mergedResults,
                    slotResults,
                  );
                }
              }
            }

            // Clear slot suggestions (we're searching, not showing suggestions)
            setSlotSuggestions([]);
            setResults(sanitizeResultsForContext(mergedResults).slice(0, 15));
          } else {
            setSlotSuggestions([]);
            setResults(sanitizeResultsForContext(searchResults).slice(0, 15)); // Limit to 15 results
          }

          setRecentlyUsedExercises([]);
          setSelectedIndex(0);
        } catch (error) {
          console.error("Exercise search error:", error);
          setResults([]);
          setRecentlyUsedExercises([]);
          setSlotSuggestions([]);
        }

        setIsSearching(false);
        setIsShowingWorkoutSuggestions(false);
      },
      [
        predefinedExercises,
        predefinedAttributes,
        searchService,
        suggestionContext,
        block,
        suggestionsMode,
        currentWorkout,
        exerciseIndex,
        t,
        language,
        sanitizeResultsForContext,
        shouldAllowWorkoutLibraryResults,
      ],
    );

    // Handle search query change with debounce
    const handleSearchChange = useCallback(
      (text) => {
        setSearchQuery(text);

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Check if text matches initial exercise name
        const matchesInitialText =
          initialSearchTextRef.current &&
          text.trim() === initialSearchTextRef.current.trim();

        // Show suggestions/recently used if text is empty
        if (!text.trim()) {
          // Workout mode: use workout-based suggestions + slot suggestions
          if (suggestionsMode === "workout" && currentWorkout && block) {
            // Only show slot suggestions when adding a new exercise (not editing)
            if (!slotExercise) {
              // Get available slots for this week
              const currentWeekNum = currentWorkout.week;
              const availableSlots = searchService.getAvailableSlots(
                block,
                currentWeekNum,
              );
              setSlotSuggestions(availableSlots);
            } else {
              setSlotSuggestions([]);
            }

            const workoutSuggestions = searchService.getWorkoutSuggestions(
              block,
              currentWorkout,
              slotExercise,
              exerciseIndex ?? 0,
            );
            const fallbackSuggestions =
              workoutSuggestions.length === 0
                ? searchService.getWorkoutFallbackSuggestions(
                    predefinedExercises,
                  )
                : workoutSuggestions;
            setResults(fallbackSuggestions);
            setRecentlyUsedExercises([]);
            setIsSearching(false);
            setSelectedIndex(0);
            setIsShowingWorkoutSuggestions(true);
            return;
          }

          setIsShowingWorkoutSuggestions(false);
          setSlotSuggestions([]);

          // Protocol mode: check for recently used exercises first
          if (block && suggestionContext) {
            const recentExercises = searchService.getRecentlyUsedExercises(
              block,
              suggestionContext,
              protocolSlot,
              currentWeek,
              slotExercise,
            );
            if (recentExercises.length > 0) {
              setRecentlyUsedExercises(recentExercises);
              setResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
              return;
            }
          }

          // Fall back to suggestions
          if (suggestionContext) {
            const suggestions = searchService.getSuggestions(
              suggestionContext,
              predefinedExercises,
              slotExercise,
            );
            setResults(suggestions);
            setRecentlyUsedExercises([]);
          } else {
            setResults([]);
            setRecentlyUsedExercises([]);
          }
          setIsSearching(false);
          setSelectedIndex(0);
          return;
        }

        // If text matches initial exercise name, try recently used/workout suggestions first
        if (matchesInitialText) {
          // Workout mode: show workout suggestions + slot suggestions
          if (suggestionsMode === "workout" && currentWorkout && block) {
            // Only show slot suggestions when adding a new exercise (not editing)
            let availableSlots = [];
            if (!slotExercise) {
              // Get available slots for this week
              const currentWeekNum = currentWorkout.week;
              availableSlots = searchService.getAvailableSlots(
                block,
                currentWeekNum,
              );
              setSlotSuggestions(availableSlots);
            } else {
              setSlotSuggestions([]);
            }

            const workoutSuggestions = searchService.getWorkoutSuggestions(
              block,
              currentWorkout,
              slotExercise,
              exerciseIndex ?? 0,
            );
            if (workoutSuggestions.length > 0 || availableSlots.length > 0) {
              const fallbackSuggestions =
                workoutSuggestions.length === 0
                  ? searchService.getWorkoutFallbackSuggestions(
                      predefinedExercises,
                    )
                  : workoutSuggestions;
              setResults(fallbackSuggestions);
              setRecentlyUsedExercises([]);
              setIsSearching(false);
              setSelectedIndex(0);
              setIsShowingWorkoutSuggestions(true);
              return;
            }
          }

          setIsShowingWorkoutSuggestions(false);
          setSlotSuggestions([]);

          // Protocol mode: try recently used
          if (block && suggestionContext) {
            const recentExercises = searchService.getRecentlyUsedExercises(
              block,
              suggestionContext,
              protocolSlot,
              currentWeek,
              slotExercise,
            );
            if (recentExercises.length > 0) {
              setRecentlyUsedExercises(recentExercises);
              setResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
              return;
            }
          }
        }

        setIsSearching(true);
        setIsShowingWorkoutSuggestions(false);
        setSlotSuggestions([]); // Clear slot suggestions when searching

        debounceTimerRef.current = setTimeout(() => {
          performSearch(text);
        }, 100);
      },
      [
        performSearch,
        suggestionContext,
        searchService,
        predefinedExercises,
        block,
        suggestionsMode,
        currentWorkout,
        exerciseIndex,
      ],
    );

    // Handle selecting a workout from library (add all exercises to current workout)
    const handleSelectWorkout = useCallback(
      (workoutResult, keepOpen = false) => {
        if (!shouldAllowWorkoutLibraryResults) {
          return;
        }

        const selectedWorkoutId = workoutResult?.workout?.id;
        if (
          selectedWorkoutId != null &&
          currentWorkout?.id != null &&
          String(selectedWorkoutId) === String(currentWorkout.id)
        ) {
          return;
        }

        if (!currentWorkout || !workoutResult.workout?.exercises) {
          onClose();
          return;
        }

        const libraryExercises = workoutResult.workout.exercises;
        if (!Array.isArray(libraryExercises) || libraryExercises.length === 0) {
          onClose();
          return;
        }

        // Check if this is the first exercise being added (before we add them)
        const existingExercises = Array.isArray(currentWorkout.exercises)
          ? currentWorkout.exercises
          : [];
        const isFirstExercise = existingExercises.length === 0;

        const canImport = canImportWorkoutExercises({
          existingCount: existingExercises.length,
          incomingCount: libraryExercises.length,
          maxExercises: WorkoutLimits.MAX_EXERCISES_PER_WORKOUT,
        });
        if (!canImport) {
          showToast?.("maxExercisesPerWorkoutReached-string", {
            type: "warning",
            title: "maxExercisesPerWorkoutReached-string",
            description: "maxExercisesPerWorkoutDescription-string",
            variables: { limit: WorkoutLimits.MAX_EXERCISES_PER_WORKOUT },
          });
          return;
        }

        // Calculate starting order for new exercises
        const currentMaxOrder = Math.max(
          0,
          ...(currentWorkout.exercises || []).map((ex) => ex.order || 0),
        );

        // Clone each exercise with new IDs
        libraryExercises.forEach((libEx, idx) => {
          const exerciseName =
            typeof libEx?.name === "string" && libEx.name.trim()
              ? libEx.name
              : libEx?.originalName || "";
          const exerciseOriginalName =
            typeof libEx?.originalName === "string" && libEx.originalName.trim()
              ? libEx.originalName
              : exerciseName;

          // Create new exercise with correct constructor signature
          const newExercise = new Exercise(
            undefined, // new ID
            exerciseName,
            exerciseOriginalName,
            libEx.notes || "",
            currentMaxOrder + idx + 1,
            true,
            false,
            currentWorkout,
            [],
            [],
            "",
            null,
            libEx.link || "",
          );

          // Create new attributes with new IDs
          const newAttributes = (libEx.attributes || []).map((libAttr) => {
            const isPredefined =
              libAttr?.isPredefined ??
              libAttr?.is_predefined ??
              libAttr?.isPredefinedAttribute ??
              false;
            return new Attribute(
              undefined, // new ID
              libAttr.name,
              libAttr.title,
              libAttr.subtitle,
              libAttr.order || 0,
              isPredefined,
              false,
              null,
              newExercise,
            );
          });
          // Create new series with new IDs, preserving all target/actual values
          const newSeries = (libEx.series || []).map((libSerie, serieIdx) => {
            const targetReps =
              libSerie?.targetReps ?? libSerie?.target_reps ?? "";
            const targetRpe = libSerie?.targetRpe ?? libSerie?.target_rpe ?? "";
            const targetWeight =
              libSerie?.targetWeight ?? libSerie?.target_weight ?? "";
            return new Serie(
              undefined,
              libSerie?.order ?? serieIdx,
              libSerie?.reps ?? 0,
              libSerie?.rpe ?? 0,
              libSerie?.weight ?? 0,
              targetReps,
              targetRpe,
              targetWeight,
              newExercise,
              libSerie?.relevance ?? 3,
              libSerie?.hasVideo ?? libSerie?.has_video ?? null,
              libSerie?.requireVideo ?? libSerie?.require_video ?? false,
            );
          });

          newExercise.series.push(...newSeries);
          newExercise.attributes.push(...newAttributes);

          // Add to current workout
          currentWorkout.exercises.push(newExercise);
        });

        // Mark workout as changed and trigger Firestore update
        currentWorkout.setProperty?.("hasChanged", true);

        // Mark tutorial step as completed if this is the first exercise added
        if (isFirstExercise) {
          const coach = block?.client?.coach || block?.coach;
          if (coach) {
            markTutorialStepCompleted(coach, showToast);
          }
        }

        if (block?.client) {
          block.client.updateClientTransactionData(block.id.toLowerCase());
          block.client.startOrResetBatchTimer();
        } else if (block?.coach) {
          block.coach.updateCoachTransactionBlocks(block.id.toLowerCase());
          block.coach.startOrResetBatchTimer();
        }

        // Close search (or reopen add flow)
        setSearchQuery("");
        setResults([]);
        setSelectedIndex(0);
        if (keepOpen && onRequestAddExercise) {
          onClose();
          setTimeout(() => {
            onRequestAddExercise();
          }, 0);
          return;
        }
        onClose();
      },
      [
        shouldAllowWorkoutLibraryResults,
        currentWorkout,
        block,
        onClose,
        onRequestAddExercise,
        showToast,
      ],
    );

    // Handle selecting a protocol slot (add slot exercises to current workout)
    const handleSelectSlot = useCallback(
      (slotResult, keepOpen = false) => {
        if (!currentWorkout || !slotResult.primaryExercise) {
          onClose();
          return;
        }

        // Calculate starting order for new exercises
        const currentMaxOrder = Math.max(
          0,
          ...(currentWorkout.exercises || []).map((ex) => ex.order || 0),
        );

        // Helper to duplicate a slot exercise
        const duplicateSlotExercise = (slotEx, orderOffset, isSecondary) => {
          // Create new series with new IDs
          const newSeries = (slotEx.series || []).map((srcSerie, serieIdx) => {
            return new Serie(
              undefined, // new ID
              serieIdx, // order
              0, // reps (actual)
              0, // rpe (actual)
              0, // weight (actual)
              srcSerie.targetReps || "",
              srcSerie.targetRpe || "",
              srcSerie.targetWeight || "",
              null, // exercise (will be set below)
              srcSerie.relevance ?? 3,
              null, // hasVideo
              srcSerie.requireVideo || false,
            );
          });

          // Create new attributes with new IDs
          const newAttributes = (slotEx.attributes || []).map((srcAttr) => {
            return new Attribute(
              undefined, // new ID
              srcAttr.name,
              srcAttr.title,
              srcAttr.subtitle,
              srcAttr.order || 0,
              srcAttr.isPredefined || false,
              false, // isSelected
              null, // predefinedExercise
              null, // exercise (will be set below)
            );
          });

          // Create new exercise
          const newExercise = new Exercise(
            undefined, // new ID
            slotEx.name,
            slotEx.originalName,
            slotEx.notes || "",
            currentMaxOrder + orderOffset, // order
            true, // isShown
            false, // isSelected
            currentWorkout, // workout
            newSeries,
            newAttributes,
            "", // exerciseToAddId
            null, // block
            slotEx.link || "",
          );

          // Set additional properties not in constructor
          newExercise.protocolSlot = slotEx.protocolSlot;
          newExercise.protocolText = slotEx.protocolText || "";
          newExercise.protocolWeek = slotEx.protocolWeek;
          newExercise.isSecondaryProtocol = isSecondary;

          // Set parent reference for series and attributes
          newSeries.forEach((s) => {
            s.exercise = newExercise;
          });
          newAttributes.forEach((attr) => {
            attr.exercise = newExercise;
          });

          return newExercise;
        };

        // Duplicate primary exercise
        const newPrimary = duplicateSlotExercise(
          slotResult.primaryExercise,
          1, // first new exercise
          false, // not secondary
        );
        currentWorkout.exercises.push(newPrimary);

        // Duplicate secondary exercise if it exists
        if (slotResult.secondaryExercise) {
          const newSecondary = duplicateSlotExercise(
            slotResult.secondaryExercise,
            2, // second new exercise
            true, // is secondary
          );
          currentWorkout.exercises.push(newSecondary);
        }

        // Mark workout as changed and trigger Firestore update
        currentWorkout.setProperty?.("hasChanged", true);

        if (block?.client) {
          block.client.updateClientTransactionData(block.id.toLowerCase());
          block.client.startOrResetBatchTimer();
        } else if (block?.coach) {
          block.coach.updateCoachTransactionBlocks(block.id.toLowerCase());
          block.coach.startOrResetBatchTimer();
        }

        // Close search (or reopen add flow)
        setSearchQuery("");
        setResults([]);
        setSelectedIndex(0);
        if (keepOpen && onRequestAddExercise) {
          onClose();
          setTimeout(() => {
            onRequestAddExercise();
          }, 0);
          return;
        }
        onClose();
      },
      [currentWorkout, block, onClose, onRequestAddExercise],
    );

    // Handle selecting a result
    const handleSelectResult = useCallback(
      (result, keepOpen = false) => {
        const fullName = buildExerciseNameWithModifiers(
          result.exerciseName,
          result.modifiers,
          language,
        );
        onSelectExercise?.({
          exerciseId: result.exerciseId,
          exerciseOriginalName: result.exerciseOriginalName,
          exerciseName: fullName,
          exerciseType: result.exerciseType,
          link: typeof result?.link === "string" ? result.link : "",
          modifiers: result.modifiers,
          keepOpen,
        });
        setSearchQuery("");
        setResults([]);
        setRecentlyUsedExercises([]);
        setSelectedIndex(0);
        if (!keepOpen) {
          onClose();
        } else {
          // Reload suggestions when keeping open
          // Use setTimeout to ensure state updates are processed first
          setTimeout(() => {
            performSearch("");
          }, 0);
        }
      },
      [onSelectExercise, onClose, language, performSearch],
    );

    // Handle selecting a recently used exercise (directly select it)
    const handleSelectRecentlyUsed = useCallback(
      (exercise, keepOpen = false) => {
        // IMPORTANT: handleSelectResult() will build the full display name by
        // appending modifiers via buildExerciseNameWithModifiers().
        // Exercise.getName() already includes modifiers, so using it here would
        // duplicate them (e.g. "Low Bar Low Bar Squat").
        const result = {
          exerciseId: exercise.id,
          exerciseOriginalName: exercise.originalName,
          exerciseName: t(exercise.originalName),
          exerciseType: exercise.type || "",
          link: typeof exercise?.link === "string" ? exercise.link : "",
          modifiers: (exercise.attributes || []).map((attr) => ({
            id: attr.id,
            originalName: attr.name,
            name: attr.getName ? attr.getName(t, language) : t(attr.name),
            type: attr.title,
            subtype: attr.subtitle || null,
          })),
        };

        // Call the same handler as regular results
        handleSelectResult(result, keepOpen);
      },
      [t, language, handleSelectResult],
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
      (e) => {
        // Calculate total items considering slots + results when showing workout suggestions
        let totalItems;
        if (recentlyUsedExercises.length > 0) {
          totalItems = recentlyUsedExercises.length;
        } else if (isShowingWorkoutSuggestions) {
          // In workout mode, slots come first, then results
          totalItems = slotSuggestions.length + results.length;
        } else {
          totalItems = results.length;
        }

        switch (e.key) {
          case "ArrowDown":
            if (totalItems > 0) {
              e.preventDefault();
              keyboardNavigationRef.current = true;
              setSelectedIndex((prev) =>
                prev < totalItems - 1 ? prev + 1 : 0,
              );
            }
            break;
          case "ArrowUp":
            if (totalItems > 0) {
              e.preventDefault();
              keyboardNavigationRef.current = true;
              setSelectedIndex((prev) =>
                prev > 0 ? prev - 1 : totalItems - 1,
              );
            }
            break;
          case "Enter":
            // Check if Shift is pressed (for "select and reopen" behavior)
            const isShiftPressed = e.shiftKey === true;
            // Only allow "select and reopen" when adding a new exercise (not editing)
            const canKeepOpen = isShiftPressed && allowShiftEnterReopen;

            if (
              recentlyUsedExercises.length > 0 &&
              recentlyUsedExercises[selectedIndex]
            ) {
              e.preventDefault();
              handleSelectRecentlyUsed(
                recentlyUsedExercises[selectedIndex],
                canKeepOpen,
              );
            } else if (isShowingWorkoutSuggestions) {
              // In workout suggestions mode: slots first, then results
              e.preventDefault();
              if (selectedIndex < slotSuggestions.length) {
                // Selected a slot
                handleSelectSlot(slotSuggestions[selectedIndex], canKeepOpen);
              } else {
                // Selected a regular suggestion
                const resultIndex = selectedIndex - slotSuggestions.length;
                if (results[resultIndex]) {
                  handleSelectResult(results[resultIndex], canKeepOpen);
                }
              }
            } else if (results.length > 0 && results[selectedIndex]) {
              e.preventDefault();
              const selectedResult = results[selectedIndex];
              // Handle different result types
              if (
                selectedResult.type === "workout" &&
                shouldAllowWorkoutLibraryResults
              ) {
                handleSelectWorkout(selectedResult, canKeepOpen);
              } else if (selectedResult.type === "slot") {
                handleSelectSlot(selectedResult, canKeepOpen);
              } else {
                handleSelectResult(selectedResult, canKeepOpen);
              }
            }
            break;
          case "Escape":
            e.preventDefault();
            resetAndClose();
            break;
        }
      },
      [
        results,
        recentlyUsedExercises,
        selectedIndex,
        resetAndClose,
        handleSelectWorkout,
        handleSelectSlot,
        handleSelectResult,
        handleSelectRecentlyUsed,
        isShowingWorkoutSuggestions,
        slotSuggestions,
        shouldAllowWorkoutLibraryResults,
      ],
    );

    // Allow keyboard navigation/select/close even when the TextInput isn't focused.
    // Do not hijack keys while:
    // - delete confirmation is shown (it has its own handler)
    // - manual picker is open (it likely manages its own keyboard interactions)
    // - user is actively typing in an editable element
    useEffect(() => {
      if (!isOpen || showDeleteConfirmation || showManualPicker) {
        return;
      }

      if (typeof window === "undefined") {
        return;
      }

      const onKeyDown = (e) => {
        const key = e?.key;
        if (
          key !== "ArrowDown" &&
          key !== "ArrowUp" &&
          key !== "Enter" &&
          key !== "Escape"
        ) {
          return;
        }

        const floatingEl = refs?.floating?.current;
        const isInsideFloating =
          !!floatingEl && typeof floatingEl.contains === "function"
            ? floatingEl.contains(e?.target)
            : false;

        // If the user is typing in *another* input outside this floating UI,
        // don't hijack arrow/enter/escape.
        if (!isInsideFloating && isEditableEventTarget(e?.target)) {
          return;
        }

        // Capture + preventDefault keeps behavior consistent regardless of focus.
        e?.preventDefault?.();
        e?.stopPropagation?.();
        handleKeyDown(e);
      };

      window.addEventListener("keydown", onKeyDown, true);
      return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [handleKeyDown, isOpen, refs, showDeleteConfirmation, showManualPicker]);

    const handleMouseMove = useCallback(() => {
      if (!isMobile) {
        keyboardNavigationRef.current = false;
      }
    }, [isMobile]);

    const handleOpenManualPicker = useCallback(() => {
      setShowDeleteConfirmation(false);
      setShowManualPicker(true);
    }, []);

    const handleCloseManualPicker = useCallback(() => {
      setShowManualPicker(false);
    }, []);

    // Handle delete exercise
    const handleDeleteExercise = useCallback(() => {
      setDeleteButtonHovered(false);
      setShowDeleteConfirmation(true);
    }, []);

    // Confirm delete action
    const handleConfirmDelete = useCallback(() => {
      if (!slotExercise) {
        setShowDeleteConfirmation(false);
        onClose();
        return;
      }

      // When opened from workout context (currentWorkout !== null), use the same logic as ExerciseCard
      // This ensures consistent behavior when deleting from ExerciseCard vs ExerciseSearchFloating
      if (currentWorkout !== null && block) {
        block.deleteWorkoutExercise(slotExercise);
      } else {
        // Protocol context (currentWorkout === null) - keep existing logic for SlotTextNew
        const isProtocolLinkedWorkoutExercise =
          !!slotExercise?.workout &&
          typeof slotExercise?.protocolSlot === "string" &&
          slotExercise.protocolSlot.trim() !== "";

        // Workout exercises can be either:
        // - protocol-linked (have protocolSlot/protocolWeek) => deleteExerciseAndSlot
        // - workout-only (no protocolSlot) => delete only from that workout
        if (slotExercise.workout) {
          if (isProtocolLinkedWorkoutExercise) {
            if (block) {
              block.deleteExerciseAndSlot(slotExercise);
            }
          } else {
            const workout = slotExercise.workout;
            const idx = Array.isArray(workout?.exercises)
              ? workout.exercises.findIndex((ex) => ex?.id === slotExercise.id)
              : -1;

            if (idx > -1) {
              workout.setProperty?.("hasChanged", true);
              workout.exercises.splice(idx, 1);
            }
          }
        } else {
          if (block) {
            // Set the selected slot before deleting
            block.setProperty("selectedSlotId", slotExercise.id.toLowerCase());
            block.deleteProtocol();
          }
        }
      }

      setShowDeleteConfirmation(false);
      onClose();
    }, [slotExercise, block, onClose, currentWorkout]);

    // Cancel delete
    const handleCancelDelete = useCallback(() => {
      setShowDeleteConfirmation(false);
    }, []);

    // Keyboard shortcuts while delete confirmation is showing:
    // - Enter: confirm delete
    // - Escape: cancel delete (go back)
    useEffect(() => {
      if (!isOpen || !showDeleteConfirmation) {
        return;
      }

      const onKeyDown = (e) => {
        const key = e?.key;
        if (key !== "Enter" && key !== "Escape") {
          return;
        }

        e?.preventDefault?.();
        e?.stopPropagation?.();

        if (key === "Enter") {
          handleConfirmDelete();
          return;
        }

        handleCancelDelete();
      };

      // Capture to beat any parent/global Escape handlers.
      window.addEventListener("keydown", onKeyDown, true);
      return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [
      handleCancelDelete,
      handleConfirmDelete,
      isOpen,
      showDeleteConfirmation,
    ]);

    // Cleanup debounce timer
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    if (!isOpen) return null;

    const hasQuery = searchQuery.trim().length > 0;
    const hasResults = results.length > 0;
    const hasRecentlyUsed = recentlyUsedExercises.length > 0;

    const shouldStabilizeWidth =
      !showManualPicker && hasQuery && !hasResults && !hasRecentlyUsed;
    const emptyStateStableWidthStyle = shouldStabilizeWidth
      ? {
          width: isMobile ? "92vw" : 360,
          maxWidth: isMobile ? 420 : 360,
        }
      : null;

    const isProtocolLinkedWorkoutExercise =
      !!slotExercise?.workout &&
      typeof slotExercise?.protocolSlot === "string" &&
      slotExercise.protocolSlot.trim() !== "";

    // Determine delete confirmation message
    const deleteTitle = isProtocolLinkedWorkoutExercise
      ? t("deleteProtocolAlertTitle-string")
      : t("deleteExerciseAlertTitle-string");

    const deleteText = isProtocolLinkedWorkoutExercise
      ? t("deleteProtocolFromWorkoutAlertText-string", {
          workoutName: slotExercise.workout?.name || "",
          itemName: slotExercise.getName(t, i18n.language),
        })
      : t("deleteExerciseAlertText-string", {
          itemName: slotExercise?.getName(t, i18n.language) || "",
        });

    const resolvedPortalRoot =
      portalRoot && portalRoot.isConnected === false ? null : portalRoot;

    return (
      <FloatingPortal root={resolvedPortalRoot ?? undefined}>
        <FloatingFocusManager
          context={context}
          modal={false}
          // Prevent auto-focusing the first tabbable element (TextInput) on mobile,
          // which triggers the virtual keyboard and can cause a flicker.
          initialFocus={isMobile ? -1 : 0}
        >
          <View
            ref={refs.setFloating}
            style={[
              {
                position: "fixed",
                zIndex: FLOATING_SEARCH_Z_INDEX,
              },
              floatingStyles,
              floatingContainerStyle,
              emptyStateStableWidthStyle,
            ]}
            {...getFloatingProps()}
          >
            {showDeleteConfirmation ? (
              <DeleteConfirmationView
                deleteTitle={deleteTitle}
                deleteText={deleteText}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                t={t}
                isMobile={isMobile}
              />
            ) : showManualPicker ? (
              /* Manual Picker View */
              <ExerciseManualPickerModal
                onBack={handleCloseManualPicker}
                initialSearchQuery={searchQuery}
                block={block}
                slotExercise={slotExercise}
                primarySlotExercise={primarySlotExercise}
                protocolSlot={protocolSlot}
                currentWeek={currentWeek}
                isSecondProtocol={instanceId?.endsWith("-second")}
                onClose={() => {
                  setSearchQuery("");
                  setResults([]);
                  setRecentlyUsedExercises([]);
                  setSelectedIndex(0);
                  setShowManualPicker(false);
                  onClose();
                }}
                onSelectExercise={(payload) => {
                  onSelectExercise?.(payload);
                }}
              />
            ) : (
              /* Search View */
              <>
                {/* Search Input Header */}
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 12,
                    backgroundColor: Colors.whiteColor,
                    borderBottomWidth: hasResults ? 0.5 : 0,
                    borderBottomColor: Colors.borderColor,
                    gap: 10,
                    outlineStyle: "none",
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      backgroundColor: Colors.lightGrayColor,
                      borderRadius: 10,
                      borderWidth: 0.5,
                      borderColor: Colors.borderColor,
                      paddingHorizontal: 10,
                      height: 36,
                    }}
                  >
                    {/* <Ionicons
                  name="search"
                  size={14}
                  color={Colors.grayColor}
                  style={{ marginRight: 8 }}
                /> */}
                    <TextInput
                      ref={searchInputRef}
                      autoFocus={!isMobile}
                      value={searchQuery}
                      onChangeText={handleSearchChange}
                      onKeyPress={handleKeyDown}
                      placeholder={
                        suggestionsMode === "workout"
                          ? `${t("searchExercise-string")} / ${t(
                              "searchWorkout-string",
                            )}`
                          : t("searchExercise-string")
                      }
                      placeholderTextColor={Colors.grayColor}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        flexShrink: 1,
                        fontSize: isMobile ? 16 : 13,
                        color: Colors.blackColor,
                        paddingVertical: 0,
                        height: "100%",
                        outlineStyle: "none",
                      }}
                      autoCorrect={false}
                      autoCapitalize="none"
                    />

                    {/* Clear icon (only when there is text) */}
                    {searchQuery.length > 0 && (
                      <TouchableOpacity
                        onPress={() => {
                          handleSearchChange("");
                          searchInputRef.current?.focus();
                        }}
                        onMouseEnter={() =>
                          !isMobile && setClearIconHovered(true)
                        }
                        onMouseLeave={() =>
                          !isMobile && setClearIconHovered(false)
                        }
                        style={{
                          padding: 2,
                          marginLeft: 6,
                        }}
                      >
                        <Ionicons
                          name="close-circle-outline"
                          size={14}
                          color={
                            clearIconHovered
                              ? Colors.darkgrayColor
                              : Colors.grayColor
                          }
                        />
                      </TouchableOpacity>
                    )}

                    {/* Manual selection icon (always visible) */}
                    <Tooltip
                      content={t("selectManually-string")}
                      placement="top"
                      delayedOpen={true}
                    >
                      <TouchableOpacity
                        onPress={handleOpenManualPicker}
                        onMouseEnter={() =>
                          !isMobile && setManualIconHovered(true)
                        }
                        onMouseLeave={() =>
                          !isMobile && setManualIconHovered(false)
                        }
                        style={{
                          padding: 2,
                          marginLeft: 6,
                        }}
                      >
                        <Ionicons
                          name="list-outline"
                          size={16}
                          color={
                            manualIconHovered
                              ? Colors.darkgrayColor
                              : Colors.grayColor
                          }
                        />
                      </TouchableOpacity>
                    </Tooltip>
                  </View>

                  {/* Delete Button (only if initialSearchText exists) */}
                  {initialSearchText != "" && initialSearchText != null && (
                    <Tooltip
                      content={t("deleteExercise-string")}
                      placement="top"
                      delayedOpen={true}
                    >
                      <TouchableOpacity
                        onPress={handleDeleteExercise}
                        onMouseEnter={() =>
                          !isMobile && setDeleteButtonHovered(true)
                        }
                        onMouseLeave={() =>
                          !isMobile && setDeleteButtonHovered(false)
                        }
                        style={{
                          padding: 4,
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: 4,
                        }}
                      >
                        <Ionicons
                          name="trash-outline"
                          size={18}
                          color={
                            deleteButtonHovered
                              ? Colors.redColor
                              : Colors.grayColor
                          }
                        />
                      </TouchableOpacity>
                    </Tooltip>
                  )}

                  <TouchableOpacity
                    onPress={onClose}
                    onMouseEnter={() =>
                      !isMobile && setCloseButtonHovered(true)
                    }
                    onMouseLeave={() =>
                      !isMobile && setCloseButtonHovered(false)
                    }
                    style={{
                      padding: 4,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Ionicons
                      name="close"
                      size={20}
                      color={
                        closeButtonHovered
                          ? Colors.darkgrayColor
                          : Colors.grayColor
                      }
                    />
                  </TouchableOpacity>
                </View>

                {/* Results List */}
                {hasRecentlyUsed && (
                  <ScrollView
                    ref={scrollViewRef}
                    onMouseMove={handleMouseMove}
                    style={{
                      flex: 1,
                      outlineStyle: "none",
                      borderTopWidth: 1,
                      borderTopColor: Colors.borderColor,
                    }}
                    contentContainerStyle={{
                      paddingTop: 12,
                      paddingBottom: 8,
                      outlineStyle: "none",
                    }}
                    keyboardShouldPersistTaps="handled"
                  >
                    {/* Section Header */}
                    <View
                      style={{
                        paddingHorizontal: 14,
                        marginHorizontal: 8,
                        // paddingTop: 4,
                        paddingBottom: 8,
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11,
                          fontWeight: "600",
                          color: Colors.grayColor,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {t("suggestions-string")}
                      </Text>
                    </View>
                    {recentlyUsedExercises.map((exercise, index) => (
                      <RecentlyUsedRow
                        key={exercise.id}
                        exercise={exercise}
                        isSelected={index === selectedIndex}
                        onSelect={() => handleSelectRecentlyUsed(exercise)}
                        onHover={() => {
                          if (!keyboardNavigationRef.current) {
                            setSelectedIndex(index);
                          }
                        }}
                        t={t}
                        language={language}
                        index={index}
                        totalResults={recentlyUsedExercises.length}
                        scrollContainerRef={scrollViewRef}
                        keyboardNavigationRef={keyboardNavigationRef}
                      />
                    ))}
                  </ScrollView>
                )}
                {(hasResults || slotSuggestions.length > 0) &&
                  isShowingWorkoutSuggestions && (
                    <ScrollView
                      ref={scrollViewRef}
                      onMouseMove={handleMouseMove}
                      style={{
                        flex: 1,
                        outlineStyle: "none",
                        borderTopWidth: 1,
                        borderTopColor: Colors.borderColor,
                      }}
                      contentContainerStyle={{
                        paddingTop: 12,
                        paddingBottom: 8,
                        outlineStyle: "none",
                      }}
                      keyboardShouldPersistTaps="handled"
                    >
                      {/* SLOTS Section (shown first if available) */}
                      {slotSuggestions.length > 0 && (
                        <>
                          {/* Slots Header */}
                          <View
                            style={{
                              paddingHorizontal: 14,
                              marginHorizontal: 8,
                              paddingBottom: 8,
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 11,
                                fontWeight: "600",
                                color: Colors.grayColor,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              {t("slots-string")}
                            </Text>
                          </View>
                          {slotSuggestions.map((slot, index) => (
                            <SlotRow
                              key={`slot-${slot.protocolSlot}-${index}`}
                              result={slot}
                              isSelected={index === selectedIndex}
                              onSelect={() => handleSelectSlot(slot)}
                              onHover={() => {
                                if (!keyboardNavigationRef.current) {
                                  setSelectedIndex(index);
                                }
                              }}
                              searchQuery=""
                              t={t}
                              language={language}
                              index={index}
                              totalResults={
                                slotSuggestions.length + results.length
                              }
                              scrollContainerRef={scrollViewRef}
                              keyboardNavigationRef={keyboardNavigationRef}
                            />
                          ))}
                        </>
                      )}

                      {/* SUGGESTIONS Section */}
                      {hasResults && (
                        <>
                          {/* Suggestions Header */}
                          <View
                            style={{
                              paddingHorizontal: 14,
                              marginHorizontal: 8,
                              paddingTop: slotSuggestions.length > 0 ? 12 : 0,
                              paddingBottom: 8,
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 11,
                                fontWeight: "600",
                                color: Colors.grayColor,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              {t("suggestions-string")}
                            </Text>
                          </View>
                          {results.map((result, index) => {
                            // Adjust index for selection (slots come first)
                            const adjustedIndex =
                              slotSuggestions.length + index;

                            // Convert result to exercise-like object for RecentlyUsedRow
                            // Build full name with modifiers
                            const fullName = buildExerciseNameWithModifiers(
                              result.exerciseName,
                              result.modifiers,
                              language,
                            );

                            const exerciseForRow = {
                              id: result.exerciseId,
                              originalName: result.exerciseOriginalName,
                              getName: () => fullName,
                            };
                            return (
                              <RecentlyUsedRow
                                key={`${result.exerciseId}-${result.modifiers
                                  .map((m) => m.id)
                                  .join("-")}-${index}`}
                                exercise={exerciseForRow}
                                isSelected={adjustedIndex === selectedIndex}
                                onSelect={() => handleSelectResult(result)}
                                onHover={() => {
                                  if (!keyboardNavigationRef.current) {
                                    setSelectedIndex(adjustedIndex);
                                  }
                                }}
                                t={t}
                                language={language}
                                index={adjustedIndex}
                                totalResults={
                                  slotSuggestions.length + results.length
                                }
                                scrollContainerRef={scrollViewRef}
                                keyboardNavigationRef={keyboardNavigationRef}
                              />
                            );
                          })}
                        </>
                      )}
                    </ScrollView>
                  )}
                {hasResults && !isShowingWorkoutSuggestions && (
                  <ScrollView
                    ref={scrollViewRef}
                    onMouseMove={handleMouseMove}
                    style={{
                      flex: 1,
                      outlineStyle: "none",
                    }}
                    contentContainerStyle={{
                      paddingVertical: 8,
                    }}
                    keyboardShouldPersistTaps="handled"
                  >
                    {results.map((result, index) => {
                      // Handle workout type results
                      if (result.type === "workout") {
                        if (!shouldAllowWorkoutLibraryResults) {
                          return null;
                        }
                        return (
                          <WorkoutLibraryRow
                            key={`workout-${result.workoutId}-${index}`}
                            result={result}
                            isSelected={index === selectedIndex}
                            onSelect={() => handleSelectWorkout(result)}
                            onHover={() => {
                              if (!keyboardNavigationRef.current) {
                                setSelectedIndex(index);
                              }
                            }}
                            searchQuery={searchQuery}
                            t={t}
                            language={language}
                            index={index}
                            totalResults={results.length}
                            scrollContainerRef={scrollViewRef}
                            keyboardNavigationRef={keyboardNavigationRef}
                          />
                        );
                      }

                      // Handle slot type results
                      if (result.type === "slot") {
                        return (
                          <SlotRow
                            key={`slot-${result.protocolSlot}-${index}`}
                            result={result}
                            isSelected={index === selectedIndex}
                            onSelect={() => handleSelectSlot(result)}
                            onHover={() => {
                              if (!keyboardNavigationRef.current) {
                                setSelectedIndex(index);
                              }
                            }}
                            searchQuery={searchQuery}
                            t={t}
                            language={language}
                            index={index}
                            totalResults={results.length}
                            scrollContainerRef={scrollViewRef}
                            keyboardNavigationRef={keyboardNavigationRef}
                          />
                        );
                      }

                      // Default: exercise result
                      return (
                        <SuggestionRow
                          key={`${result.exerciseId}-${(result.modifiers || [])
                            .map((m) => m.id)
                            .join("-")}-${index}`}
                          result={result}
                          isSelected={index === selectedIndex}
                          onSelect={() => handleSelectResult(result)}
                          onHover={() => {
                            if (!keyboardNavigationRef.current) {
                              setSelectedIndex(index);
                            }
                          }}
                          searchQuery={searchQuery}
                          t={t}
                          language={language}
                          index={index}
                          totalResults={results.length}
                          scrollContainerRef={scrollViewRef}
                          keyboardNavigationRef={keyboardNavigationRef}
                        />
                      );
                    })}

                    {/* Footer hint at end of list */}
                    <View
                      style={{
                        paddingVertical: 12,
                        paddingHorizontal: 12,
                        alignItems: "center",
                        // marginTop: 8,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11,
                          color: Colors.grayColor,
                          textAlign: "center",
                        }}
                      >
                        {t("notFindingWhatYouWant-string")}{" "}
                        <Text
                          onPress={handleOpenManualPicker}
                          onMouseEnter={() =>
                            !isMobile && setManualLinkFooterHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setManualLinkFooterHovered(false)
                          }
                          style={{
                            color: manualLinkFooterHovered
                              ? MANUAL_HOVER_COLOR
                              : Colors.bubbleColor,
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          {t("selectExerciseManually-string")}
                        </Text>
                      </Text>
                    </View>
                  </ScrollView>
                )}

                {/* Searching Placeholder (keeps panel content stable during debounce) */}
                {hasQuery && !hasResults && !hasRecentlyUsed && isSearching && (
                  <View
                    style={{
                      flex: 1,
                      paddingVertical: 26,
                      // marginTop: 4,
                      paddingHorizontal: 24,
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    <CustomActivityIndicator large={false} white={false} />
                  </View>
                )}

                {/* No Results */}
                {hasQuery &&
                  !hasResults &&
                  !hasRecentlyUsed &&
                  !isSearching && (
                    <View
                      style={{
                        paddingVertical: 26,
                        marginTop: 8,
                        paddingHorizontal: 24,
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 13,
                          color: Colors.grayColor,
                          lineHeight: 18,
                          textAlign: "center",
                          maxWidth: "100%",
                        }}
                      >
                        {t("notFindingWhatYouWant-string")}{" "}
                        <Text
                          onPress={handleOpenManualPicker}
                          onMouseEnter={() =>
                            !isMobile && setManualLinkEmptyHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setManualLinkEmptyHovered(false)
                          }
                          style={{
                            color: manualLinkEmptyHovered
                              ? MANUAL_HOVER_COLOR
                              : Colors.bubbleColor,
                            fontWeight: "600",
                            cursor: "pointer",
                            flexShrink: 1,
                          }}
                        >
                          {t("selectExerciseManually-string")}
                        </Text>
                      </Text>
                    </View>
                  )}

                {/* Footer hint + keyboard hint */}
                {(hasResults || hasRecentlyUsed) && !isMobile && (
                  <View
                    style={{
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      borderTopWidth: 0.5,
                      borderTopColor: Colors.borderColor,
                      outlineStyle: "none",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 10,
                        color: Colors.grayColor,
                        textAlign: "center",
                      }}
                    >
                      ↑↓ {t("navigate-string")}
                      {"    "}↵ {t("select-string")}
                      {allowShiftEnterReopen && (
                        <>
                          {"    "}⇧↵ {t("selectAndReopen-string")}
                        </>
                      )}
                      {"    "}Esc {t("close-string")}
                    </Text>
                  </View>
                )}
              </>
            )}
          </View>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  },
);

export default ExerciseSearchFloating;
